import { duration, formatMediumTime } from "@/format/time";
import type { LogGroup, LogGroups, SectionLogLine, LegacyLogLine } from "@/routes/environments/log-streams/log-groups";
import { NO_LOG_GROUP } from "@/routes/environments/log-streams/log-groups";

/**
 * Formats a single log line for copying, including timestamp if available.
 */
export function formatLogLineForCopy(line: SectionLogLine | LegacyLogLine): string {
    if (line.timestamp) {
        return `${formatMediumTime(line.timestamp)} ${line.line}`;
    }
    return line.line;
}

/**
 * Formats a log group header for copying, including duration if available.
 */
export function formatLogGroupHeader(logGroup: LogGroup): string {
    const durationSuffix = logGroup.secondsElapsed ? ` (${duration(logGroup.secondsElapsed)})` : "";
    return `=== ${logGroup.title}${durationSuffix} ===`;
}

/**
 * Formats all lines in a log group for copying, with header.
 */
export function formatLogGroupForCopy(logGroup: LogGroup, validLines: (SectionLogLine | LegacyLogLine)[]): string {
    const header = formatLogGroupHeader(logGroup);
    const lines = validLines.map(formatLogLineForCopy);
    return [header, ...lines].join("\n");
}

/**
 * Formats all log groups for copying.
 */
export function formatAllLogsForCopy(logGroups: LogGroups, isLegacyRunner: boolean): string {
    const allLogLines: string[] = [];

    if (isLegacyRunner) {
        const lines = logGroups[NO_LOG_GROUP]?.lines ?? [];
        for (const line of lines) {
            if (line.type === "SectionLogLine" || line.type === "LegacyLogLine") {
                allLogLines.push(formatLogLineForCopy(line));
            }
        }
    } else {
        for (const logGroup of Object.values(logGroups)) {
            if (logGroup.id === NO_LOG_GROUP) continue;

            allLogLines.push(formatLogGroupHeader(logGroup));

            for (const line of logGroup.lines) {
                if (line.type === "SectionLogLine" || line.type === "LegacyLogLine") {
                    allLogLines.push(formatLogLineForCopy(line));
                }
            }

            allLogLines.push("");
        }
    }

    return allLogLines.join("\n");
}
